'use client';

import base, { type AxiosInstance } from 'axios';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { AuthContext, clearStorage } from './Authentication';
import { useRBACOptional } from '@/contexts/RBACContext';
import { fetchSubTenantMemberUserIds } from '@/lib/subTenantMemberUserIds';
import { listAllAccounts } from '@/services/rbacApi';
import { getActiveTenantHeaders, resolveActiveRequestUserId } from '@/lib/activeTenantScope';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { apiConfig } from '@/config/apiConfig';

export const EmailAxiosContext = createContext<AxiosInstance | null>(null);

/** SWR/cache key segment: sub-tenant scope / member IDs / account reset. */
export const EmailSubTenantMemberKeyContext = createContext<string>('');

/**
 * True once the org-owner's full member IDs have been fetched (or confirmed unnecessary).
 * Gates single-item campaign fetches so a member-created campaign is resolved before the request fires.
 */
export const EmailOwnerMemberIdsReadyContext = createContext<boolean>(true);

function normalizeRequestPath(url: string | undefined): string {
  if (!url) return '';
  const noQuery = url.split('?')[0];
  if (noQuery.includes('://')) {
    try {
      return new URL(noQuery).pathname.replace(/^\/+|\/+$/g, '') || '';
    } catch {
      return noQuery.replace(/^\/+|\/+$/g, '');
    }
  }
  return noQuery.replace(/^\/+|\/+$/g, '');
}

/** Add member_user_ids query for org-owner sub-tenant views (email Lambda paths). */
function emailPathNeedsMemberScope(pathNorm: string, method: string): boolean {
  const m = method.toLowerCase();
  const p = pathNorm.replace(/^\/+|\/+$/g, '');
  if (!p.includes('api/v1/')) return false;

  if (/(^|\/)api\/v1\/campaigns\/?$/.test(p) && m === 'get') return true;
  if (/(^|\/)api\/v1\/campaigns\/[^/]+\/send\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)api\/v1\/campaigns\/[^/]+\/send-test\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)api\/v1\/campaigns\/[^/]+\/duplicate\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)api\/v1\/campaigns\/[^/]+\/?$/.test(p) && (m === 'get' || m === 'patch' || m === 'delete'))
    return true;
  if (/(^|\/)email\/campaign\/action\/bulk\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)email\/campaign\/analytics\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)email\/campaign\/analytics\/(organisation|export)\/?$/.test(p) && m === 'post') return true;

  if (/(^|\/)api\/v1\/templates\/?$/.test(p) && m === 'get') return true;
  if (/(^|\/)api\/v1\/templates\/categories\/?$/.test(p) && m === 'get') return true;
  if (/(^|\/)api\/v1\/templates\/[^/]+\/preview\/?$/.test(p) && m === 'post') return true;
  if (/(^|\/)api\/v1\/templates\/[^/]+\/?$/.test(p) && (m === 'get' || m === 'patch' || m === 'delete'))
    return true;

  if (/(^|\/)api\/v1\/email-accounts\/?$/.test(p) && m === 'get') return true;
  if (/(^|\/)api\/v1\/email-accounts\/[^/]+\/stats\/?$/.test(p) && m === 'get') return true;
  if (/(^|\/)api\/v1\/email-accounts\/[^/]+\/?$/.test(p) && (m === 'get' || m === 'patch' || m === 'delete'))
    return true;

  return false;
}

function getEmailInstance(
  auth: {
    userId?: string | number | null;
    token?: string | null;
    isAuthenticated?: boolean;
    isLoading?: boolean;
  },
  setSessionExpiredOpen: (open: boolean) => void,
  aggregateMemberIdsRef: MutableRefObject<number[] | null>,
  subTenantScopeActiveRef: MutableRefObject<boolean>,
  ownerDefaultMemberIdsRef: MutableRefObject<number[] | null>,
  isOrgOwnerRef: MutableRefObject<boolean>
) {
  const baseURL = apiConfig.baseUrls.email;
  const axios = base.create({
    baseURL: baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL,
    withCredentials: true,
  });

  axios.interceptors.request.use((req) => {
    const activeUserId = resolveActiveRequestUserId(auth?.userId);
    if (req.data && typeof req.data === 'object' && !(req.data instanceof FormData)) {
      if (!req.data.userID && activeUserId) req.data.userID = activeUserId;
      if (auth?.isAuthenticated && !req.data.session_token && auth?.token) {
        req.data.session_token = auth.token;
      }
    }
    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    Object.assign(req.headers, getActiveTenantHeaders());
    if (activeUserId) req.headers['X-User-ID'] = String(activeUserId);
    if (auth?.isAuthenticated && auth?.token) {
      req.headers['Authorization'] = `Bearer ${auth.token}`;
    }
    if (typeof document !== 'undefined' && document.cookie) {
      req.headers['cookies'] = document.cookie;
    }

    const method = (req.method || 'get').toLowerCase();
    const pathNorm = normalizeRequestPath(req.url);
    // Single-item campaign GET: org owner in default context needs member IDs so
    // campaigns created by org members can be fetched by URL.
    const isSingleCampaignGet =
      method === 'get' && /(^|\/)api\/v1\/campaigns\/[^/]+\/?$/.test(pathNorm.replace(/^\/+|\/+$/g, ''));
    if (subTenantScopeActiveRef.current && emailPathNeedsMemberScope(pathNorm, method)) {
      req.params = req.params || {};
      const ids = aggregateMemberIdsRef.current ?? [];
      (req.params as Record<string, string>).member_user_ids = ids.map(String).join(',');
    } else if (!subTenantScopeActiveRef.current && isOrgOwnerRef.current && isSingleCampaignGet) {
      const fallback = ownerDefaultMemberIdsRef.current;
      if (fallback != null) {
        req.params = req.params || {};
        (req.params as Record<string, string>).member_user_ids = fallback.map(String).join(',');
      }
    } else if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('impersonation') === 'true' &&
      activeUserId &&
      emailPathNeedsMemberScope(pathNorm, method)
    ) {
      req.params = req.params || {};
      (req.params as Record<string, string>).member_user_ids = String(activeUserId);
    } else if (req.params && typeof req.params === 'object' && 'member_user_ids' in req.params) {
      delete (req.params as Record<string, unknown>).member_user_ids;
    }

    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (auth?.isAuthenticated) {
            location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
          } else {
            setSessionExpiredOpen(true);
          }
        }
      }
      return Promise.reject(err);
    }
  );
  return axios;
}

export default function EmailAxios({ children }: { children: React.ReactNode }) {
  const authData = useContext(AuthContext);
  const rbac = useRBACOptional();
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [subTenantMemberKey, setSubTenantMemberKey] = useState('');
  const aggregateMemberIdsRef = useRef<number[] | null>(null);
  const subTenantScopeActiveRef = useRef(false);

  const ownerDefaultMemberIdsRef = useRef<number[] | null>(null);
  const isOrgOwnerRef = useRef<boolean>(false);
  const ownerUserIdRef = useRef<number | null>(null);
  isOrgOwnerRef.current = Boolean(rbac?.userContext?.is_org_owner);
  ownerUserIdRef.current = rbac?.userContext?.owner_user_id ? Number(rbac.userContext.owner_user_id) : null;
  const [ownerMemberIdsReady, setOwnerMemberIdsReady] = useState(false);

  const listingSubAccountId = rbac?.effectiveListingSubAccountId ?? null;
  const isSubTenantListingScope = Boolean(listingSubAccountId);
  if (!isSubTenantListingScope) {
    aggregateMemberIdsRef.current = null;
  }
  // Only activate the scope interceptor branch after member IDs have fully loaded.
  // Setting this true before the async fetch resolves causes the interceptor to send
  // member_user_ids="" (empty), which makes the backend return 0 rows.
  const subTenantMemberListReady =
    isSubTenantListingScope &&
    Boolean(subTenantMemberKey) &&
    !subTenantMemberKey.startsWith('loading:');
  subTenantScopeActiveRef.current = subTenantMemberListReady;

  useEffect(() => {
    if (!listingSubAccountId) {
      aggregateMemberIdsRef.current = null;
      setSubTenantMemberKey('');
      return;
    }
    const subId = listingSubAccountId;
    const orgId = rbac?.currentOrgId;
    aggregateMemberIdsRef.current = [];
    setSubTenantMemberKey(`loading:${subId}`);
    let cancelled = false;
    (async () => {
      try {
        const ids = await fetchSubTenantMemberUserIds(subId, {
          orgId,
          bypassCache: true,
        });
        // Non-owner org members have their own account_id as effectiveListingSubAccountId,
        // which isn't a real sub-tenant — fetchSubTenantMemberUserIds returns [].
        // Fall back to the member's own user ID so the backend can scope to their data.
        const effectiveIds =
          ids.length > 0 || isOrgOwnerRef.current
            ? ids
            : authData?.userId
              ? [Number(authData.userId)]
              : [];
        if (!cancelled) {
          aggregateMemberIdsRef.current = effectiveIds;
          const sorted = [...effectiveIds].sort((a, b) => a - b);
          setSubTenantMemberKey(`${subId}:${sorted.join(',')}`);
        }
      } catch {
        if (!cancelled) {
          aggregateMemberIdsRef.current = [];
          setSubTenantMemberKey(`${subId}:`);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [listingSubAccountId, rbac?.currentOrgId, authData?.userId]);

  // Sweep all org accounts so the owner can resolve member-created campaigns by URL.
  useEffect(() => {
    if (rbac?.isLoading) return;
    const isOwner = Boolean(rbac?.userContext?.is_org_owner);
    if (!isOwner) {
      ownerDefaultMemberIdsRef.current = null;
      setOwnerMemberIdsReady(true);
      return;
    }
    const orgId = rbac?.currentOrgId ?? null;
    if (!orgId) {
      const ownerExtId = ownerUserIdRef.current;
      ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
      setOwnerMemberIdsReady(true);
      return;
    }
    setOwnerMemberIdsReady(false);
    let cancelled = false;
    (async () => {
      try {
        const allIds: number[] = [];
        const ownerExtId = ownerUserIdRef.current;
        if (ownerExtId != null && Number.isFinite(ownerExtId)) allIds.push(ownerExtId);
        const accounts = await listAllAccounts(orgId);
        await Promise.all(
          accounts.map(async (account) => {
            try {
              const ids = await fetchSubTenantMemberUserIds(account.id, { orgId, bypassCache: false });
              for (const id of ids) {
                if (!allIds.includes(id)) allIds.push(id);
              }
            } catch { /* skip */ }
          })
        );
        if (!cancelled) {
          ownerDefaultMemberIdsRef.current = allIds;
          setOwnerMemberIdsReady(true);
        }
      } catch {
        if (!cancelled) {
          const ownerExtId = ownerUserIdRef.current;
          ownerDefaultMemberIdsRef.current = ownerExtId != null ? [ownerExtId] : [];
          setOwnerMemberIdsReady(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [rbac?.isLoading, rbac?.userContext?.is_org_owner, rbac?.currentOrgId]);

  const auth = authData || {};
  const client = useMemo(
    () => getEmailInstance(auth, setSessionExpiredOpen, aggregateMemberIdsRef, subTenantScopeActiveRef, ownerDefaultMemberIdsRef, isOrgOwnerRef),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recreate when auth identity changes
    [authData]
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    location.href = process.env.NEXT_PUBLIC_SELF_URL + '/v2/login';
  };

  // Use a loading: sentinel while IDs are pending so SWR consumers don't fire prematurely.
  const effectiveMemberKey =
    !isSubTenantListingScope ? ''
    : !subTenantMemberKey || subTenantMemberKey.startsWith('loading:')
      ? `loading:${listingSubAccountId}`
      : subTenantMemberKey;
  const emailSubTenantScopeSignal = [
    effectiveMemberKey,
    String(listingSubAccountId ?? ''),
    String(rbac?.currentAccountId ?? ''),
    String(rbac?.defaultAccountId ?? ''),
  ].join('|');

  return (
    <>
      <EmailAxiosContext.Provider value={client}>
        <EmailSubTenantMemberKeyContext.Provider value={emailSubTenantScopeSignal}>
          <EmailOwnerMemberIdsReadyContext.Provider value={ownerMemberIdsReady}>
            {children}
          </EmailOwnerMemberIdsReadyContext.Provider>
        </EmailSubTenantMemberKeyContext.Provider>
      </EmailAxiosContext.Provider>
      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">Session Expired</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">Your session has expired. Please log in again to continue.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
